import React from "react";

/**
 * The desktop editor spends ~556px on the pages rail + inspector before the
 * preview gets any room, so it stops being usable well above phone widths.
 */
export const WEBSITE_EDITOR_MOBILE_MAX_PX = 900;
/** Must grow past this to leave the mobile chrome (avoids flip-flop on drag-resize). */
export const WEBSITE_EDITOR_MOBILE_EXIT_PX = 940;

function matches(query: string): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(query).matches;
}

function useMediaQuery(query: string): boolean {
  const [on, setOn] = React.useState(() => matches(query));

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setOn(event.matches);
    setOn(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return on;
}

/**
 * True while the dashboard website editor should render its mobile shell.
 * Uses enter/exit hysteresis so dragging across ~900px does not remount the
 * preview tree on every pixel.
 */
export function useWebsiteEditorMobile(
  maxWidth: number = WEBSITE_EDITOR_MOBILE_MAX_PX,
  exitWidth: number = WEBSITE_EDITOR_MOBILE_EXIT_PX,
): boolean {
  const enterAt = maxWidth;
  const leaveAt = Math.max(maxWidth + 1, exitWidth);

  const [mobile, setMobile] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= enterAt;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const apply = () => {
      const w = window.innerWidth;
      setMobile((prev) => {
        if (prev) return w < leaveAt;
        return w <= enterAt;
      });
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener("resize", schedule, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
    };
  }, [enterAt, leaveAt]);

  return mobile;
}

/**
 * Primary input cannot hover — hover-to-reveal chrome never fires on its own.
 * Used to swap the editor's hover affordances for explicit taps.
 *
 * Intentionally `(hover: none)` only — do NOT OR `(pointer: coarse)`. Hybrid
 * laptops/tablets often report a coarse pointer *and* hover; matching coarse
 * alone kept “Add component” lines permanently visible on desktop (Loom).
 */
export function useCoarsePointer(): boolean {
  return useMediaQuery("(hover: none)");
}

/**
 * Holds the document at the top for as long as the mobile editor is mounted.
 *
 * Tapping a section to edit it opens the keyboard, and WebKit answers by
 * scrolling the document to reveal the focused field. The page title slides
 * under the sticky top nav, and the offset outlives the keyboard: dismissing it
 * and publishing both leave the title sliced in half. The editor has no use for
 * a document scroll — the preview canvas is its own scroll surface and the
 * chrome is pinned — so any offset here came from the browser, and taking it
 * back is safe.
 *
 * `Dashboard.css` also takes the body out of flow on this route, which stops
 * most of it at the source. This stays as the backstop for the cases that rule
 * cannot reach: browsers without `:has()`, and the builder variant it excludes.
 */
export function useDocumentScrollPinned(): void {
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    /*
     * Corrects synchronously rather than on the next frame. Deferring leaves the
     * page visibly displaced for a frame or more, and a `requestAnimationFrame`
     * is throttled to nothing whenever the tab is not being painted — the offset
     * then sits there until the tab wakes up. The work is two reads and, only
     * when something has actually moved, one write.
     */
    const reset = () => {
      const doc = document.scrollingElement || document.documentElement;
      // Re-entrant by design: the corrective scroll fires another scroll event,
      // which lands back here, sees zero, and stops.
      if (window.scrollY === 0 && doc.scrollTop === 0) return;
      doc.scrollTop = 0;
      window.scrollTo(0, 0);
    };

    // Reacting to the scroll itself avoids guessing how long iOS takes to reveal
    // a focused field, which varies with the keyboard animation.
    window.addEventListener("scroll", reset);
    window.visualViewport?.addEventListener("resize", reset);
    window.visualViewport?.addEventListener("scroll", reset);

    return () => {
      window.removeEventListener("scroll", reset);
      window.visualViewport?.removeEventListener("resize", reset);
      window.visualViewport?.removeEventListener("scroll", reset);
    };
  }, []);
}

/**
 * Nearest ancestor that will clip this element's overflowing bottom edge.
 * Returned so the shell can size itself to fit rather than be cut off by it.
 */
function clippingAncestor(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY !== "visible") return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Pins an element's height to the gap between its own top edge and the bottom
 * of the visible viewport.
 *
 * The shell sits under six ancestors (`.dashboard` → `.dashboard-layout` →
 * `.content-container` → `.row` → `.right-panel` → `.website-dashboard--shell`
 * → `.details` → `.website-dashboard__editor`) that each re-declare height and
 * overflow across three breakpoints. If any one of them loses its viewport lock
 * the shell overflows, which pushes the bottom action bar off-screen and hands
 * the page an outer scroll — the editor's own chrome then scrolls away with it.
 * Deriving the height from a measurement instead of the cascade makes the shell
 * correct no matter what the ancestors do.
 *
 * `visualViewport` is preferred over `innerHeight` so the shell tracks the
 * on-screen keyboard and collapsing mobile browser chrome.
 */
export function useViewportAnchoredHeight(
  ref: React.RefObject<HTMLElement>,
): number | null {
  const [height, setHeight] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    let frame = 0;

    const measure = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const vv = window.visualViewport;
      const viewportHeight = vv ? vv.height : window.innerHeight;
      const viewportBottom = vv ? vv.height + vv.offsetTop : window.innerHeight;
      // Measure against the element's own top, not the parent's, so any
      // unaccounted chrome above (top nav, page title, banners) is excluded.
      const top = el.getBoundingClientRect().top;
      const gap = viewportBottom - top;
      /*
       * The shell sits inside `.website-dashboard__editor`, which is
       * `overflow: hidden` and sized by the flex chain off `.dashboard`'s
       * `100dvh`. iOS reports `dvh` and `visualViewport` differently while the
       * browser chrome animates, so a shell measured only against the viewport
       * can end up taller than the box that clips it — and the clipped edge is
       * the bottom, which is exactly where the action bar lives.
       */
      const clipper = clippingAncestor(el);
      const clipRoom = clipper
        ? clipper.getBoundingClientRect().bottom - top
        : Infinity;
      /*
       * Capping at the viewport breaks a runaway loop: if anything does scroll
       * the page, the shell's top goes negative, the raw gap grows, the taller
       * shell creates more scrollable overflow, and the next scroll grows it
       * again — which is how the title scrolls away and the action bar ends up
       * under the browser chrome. The shell is never usefully taller than the
       * screen, so clamping costs nothing and makes the measurement stable.
       */
      const next = Math.round(Math.min(gap, viewportHeight, clipRoom));
      // A collapsed ancestor can briefly report a nonsense top mid-layout.
      if (!Number.isFinite(next) || next < 240) return;
      setHeight((prev) => (prev !== null && Math.abs(prev - next) < 1 ? prev : next));
    };

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    schedule();

    const observer = new ResizeObserver(schedule);
    if (ref.current?.parentElement) observer.observe(ref.current.parentElement);
    const clipper = ref.current ? clippingAncestor(ref.current) : null;
    if (clipper) observer.observe(clipper);
    observer.observe(document.documentElement);

    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    /*
     * Publishing swaps the action bar's buttons and drops the rebrand banner,
     * which moves the shell's top edge without resizing any ancestor. Watching
     * scroll in the capture phase also catches an ancestor that slips its lock.
     */
    window.addEventListener("scroll", schedule, true);
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      window.removeEventListener("scroll", schedule, true);
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
    };
  }, [ref]);

  return height;
}
